import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert, StatusBadge } from '../../components/UI';

// jsPDF is ~130 kB gzipped, so it is only fetched when a card is actually asked for.
async function makeProgressCard(data) {
  const { downloadProgressCard } = await import('../../utils/progressCard');
  await downloadProgressCard(data);
}

export default function AttemptDetail() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [edits, setEdits] = useState({});
  const [filter, setFilter] = useState('all'); // all | correct | incorrect

  const load = useCallback(() => api.get(`/results/attempts/${attemptId}`).then(setData).catch(e => setError(e.message)), [attemptId]);
  useEffect(() => { load(); }, [load]);

  async function overrideScore(answerId, maxScore) {
    const val = edits[answerId];
    if (val === undefined) return;
    try {
      await api.put(`/results/answers/${answerId}`, { awarded_score: Number(val) });
      await load();
    } catch (e) { setError(e.message); }
  }

  async function publish() {
    try { await api.post('/results/publish', { attempt_ids: [Number(attemptId)] }); await load(); }
    catch (e) { setError(e.message); }
  }

  if (!data && !error) return <Spinner />;
  if (error && !data) return <Alert type="danger">{error}</Alert>;

  const totalQuestions = data.answers.length;
  const attempted = data.answers.filter(
    a => a.selected_option_id != null || (a.answer_text && a.answer_text.trim() !== '')
  ).length;

  // Full marks counts as correct; anything short of that (including partial credit
  // on an AI-scored text answer) counts as incorrect.
  const isCorrect = a => Number(a.awarded_score) >= Number(a.max_score);
  const correctCount = data.answers.filter(isCorrect).length;
  const visibleAnswers = data.answers.filter(a =>
    filter === 'all' || (filter === 'correct' ? isCorrect(a) : !isCorrect(a))
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow"><Link to="/admin/results">&larr; Results</Link></div>
          <h1>{data.user_name}</h1>
          <p className="mono text-sm">{data.mobile} &middot; {data.paper_title}</p>
        </div>
        <div className="flex-gap">
          <StatusBadge status={data.status} />
          {data.status === 'evaluated' && !data.is_published && (
            <button className="btn btn-primary" onClick={publish}>Publish result</button>
          )}
          {data.is_published && (
            <button
              className="btn btn-outline"
              onClick={() => makeProgressCard(data).catch(e => setError(e.message))}
            >
              Download progress card
            </button>
          )}
        </div>
      </div>

      <Alert type="danger">{error}</Alert>

      <div className="stat-grid">
        <div className="stat-card"><div className="label">Candidate</div><div className="value" style={{ fontSize: 'var(--fs-lg)' }}>{data.user_name}</div><div className="sub mono">{data.mobile}</div></div>
        <div className="stat-card"><div className="label">Questions attempted</div><div className="value">{attempted} / {totalQuestions}</div><div className="sub">{totalQuestions - attempted} left blank</div></div>
        <div className="stat-card"><div className="label">Total score</div><div className="value">{data.total_score} / {data.max_score}</div></div>
        <div className="stat-card"><div className="label">Tab switches</div><div className="value">{data.tab_switches}</div></div>
        <div className="stat-card"><div className="label">Submitted</div><div className="value" style={{ fontSize: 'var(--fs-lg)' }}>{data.submitted_at ? new Date(data.submitted_at).toLocaleString('en-IN') : '-'}</div></div>
      </div>

      <div className="tabs">
        <div className={`tab ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
          All {totalQuestions}
        </div>
        <div className={`tab ${filter === 'correct' ? 'active' : ''}`} onClick={() => setFilter('correct')}>
          Correct {correctCount}
        </div>
        <div className={`tab ${filter === 'incorrect' ? 'active' : ''}`} onClick={() => setFilter('incorrect')}>
          Incorrect {totalQuestions - correctCount}
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Dept</th><th>Question</th><th>Answer</th><th>Result</th><th>Validated by</th><th>Review</th><th>Score</th><th></th></tr></thead>
          <tbody>
            {visibleAnswers.map(a => (
              <tr key={a.id}>
                <td className="text-sm text-muted">{a.department_name}</td>
                <td style={{ maxWidth: 280 }}>{a.question_text}</td>
                <td className="text-sm">{a.selected_option_text || a.answer_text || '-'}</td>
                <td>
                  <span className={`badge ${isCorrect(a) ? 'badge-success' : 'badge-danger'}`}>
                    {isCorrect(a) ? 'Correct' : 'Incorrect'}
                  </span>
                </td>
                <td><StatusBadge status={a.validated_by || 'system'} /></td>
                <td><StatusBadge status={a.review_status} /></td>
                <td>
                  <input
                    type="number" min={0} max={a.max_score} step={0.5} style={{ width: 80 }}
                    value={edits[a.id] ?? a.awarded_score}
                    onChange={e => setEdits(s => ({ ...s, [a.id]: e.target.value }))}
                  /> / {a.max_score}
                </td>
                <td>
                  <button className="btn btn-outline btn-sm" onClick={() => overrideScore(a.id, a.max_score)}>Save</button>
                </td>
              </tr>
            ))}
            {visibleAnswers.length === 0 && (
              <tr><td colSpan={8} className="text-sm text-muted">
                {filter === 'correct' ? 'No questions were answered correctly.' : 'Every question was answered correctly.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
